/**
 * Derived-fact helpers for the human call card (ADR-0163 §1 slot ③).
 *
 * Every fact reads from a TYPED source (`live.mcp`, `liveProducts`,
 * `cart`, `endpoint.userAgent`, `endpoint.masqueradeNote`) — never a
 * builder-authored string. This is the honesty invariant that killed
 * the `Phase.agentSays` field (ADR-0163 §5): builder strings drift
 * from what actually happened; typed derivation cannot.
 *
 * Pure module: no React, no hooks, no fetches. A phase kind with no
 * derivable fact yields zero fact lines — the outcome line stands
 * alone (safe fallback).
 */

import type { LiveResult } from '../live/fetch-surface.js';
import type { CartProduct, EndpointInfo, LiveProducts } from '../journeys/types.js';
import { summarizeProducts as summarizeProductSamples } from '../journeys/discovery.js';

export interface Fact {
  /** Mono kicker text (e.g. `Requester`, `Tools`, `Products`). */
  readonly label: string;
  /** Geist value text. */
  readonly value: string;
  /** `amber` marks the masquerade note; default otherwise. */
  readonly tone?: 'default' | 'amber';
  /**
   * Optional small muted caption under the value line — carries the
   * honest documented-UA framing in human mode (test-pinned copy).
   */
  readonly caption?: string;
  /**
   * Optional full-fidelity mono line under the caption (the complete
   * documented UA). ADR-0163 §5: the full UA stays VISIBLE in human
   * mode — pinned by the Grok `Version/17.4 Safari/605.1.15` test.
   */
  readonly detail?: string;
  /**
   * When present, the fact is a "N exposed: <top-3> · +K more" summary
   * and this field carries the FULL ordered id list. The renderer turns
   * the "+K more" tail into an inline expand/collapse button so the
   * merchant can reveal every id without leaving the row. Absent when
   * the list already fits (≤ 3 ids) or the fact isn't list-shaped —
   * plain `value` rendering is unaffected. Only set by the two skill-
   * summary facts today (`cardSkillsFact`, `parseToolNames`).
   */
  readonly expandable_ids?: readonly string[];
}

/**
 * Extract the tool names from a live `tools/list` JSON-RPC response
 * body. `null` = not a parseable tools/list result (error body,
 * network failure text, …). An empty array is a REAL answer — the
 * site exposes zero tools — and is distinct from `null`.
 *
 * Moved here from `journeys/claude.ts` (ADR-0163 §8) so the journey's
 * skill-aware gate and the fact derivation read one parser.
 */
export function extractToolNames(body: string): string[] | null {
  try {
    return toolNamesFromParsed(JSON.parse(body));
  } catch {
    return null;
  }
}

function toolNamesFromParsed(parsed: unknown): string[] | null {
  const tools = (parsed as { result?: { tools?: Array<{ name?: unknown }> } } | null)?.result
    ?.tools;
  if (!Array.isArray(tools)) return null;
  return tools
    .map((t) => (typeof t.name === 'string' ? t.name : null))
    .filter((n): n is string => n !== null);
}

/**
 * Tool names from a {@link LiveResult} — prefers the pre-truncation
 * `json` (the display `body` is capped at 900 chars, so parsing it
 * silently fails on large responses); falls back to the body parse for
 * results built before the `json` field existed (tests, cached shapes).
 */
export function toolNamesFromResult(result: LiveResult): string[] | null {
  if (result.json !== undefined) return toolNamesFromParsed(result.json);
  return extractToolNames(result.body);
}

/**
 * MCP `tools/list` → `Tools: browse_catalog · check_availability · … +N more`.
 * A live EMPTY answer is a real, actionable fact — every skill is
 * toggled off — and renders amber with the settings pointer instead of
 * disappearing (founder feedback 2026-07-05: the view must reflect the
 * merchant's skill toggles and hint at why).
 */
export function parseToolNames(mcp: LiveResult | undefined): Fact | null {
  if (mcp === undefined || !mcp.ok) return null;
  const names = toolNamesFromResult(mcp);
  if (names === null) return null;
  if (names.length === 0) {
    return {
      label: 'Tools',
      value: '0 exposed — no skill is live for this site (Settings → Skills shows why).',
      tone: 'amber',
    };
  }
  const shown = names.slice(0, 3);
  const hasMore = names.length > shown.length;
  const suffix = hasMore ? ` · +${names.length - shown.length} more` : '';
  return {
    label: 'Tools',
    value: `${names.length} exposed: ${shown.join(' · ')}${suffix}`,
    ...(hasMore ? { expandable_ids: names } : {}),
  };
}

/**
 * Extract the skill ids from a LIVE signed agent-card body (the A2A v2
 * card carries a top-level `skills[]` with `id` per entry — ADR-0116
 * §4.4). `null` = not a parseable card. Empty = the card truthfully
 * advertises nothing.
 */
export function extractCardSkills(body: string): string[] | null {
  try {
    return cardSkillsFromParsed(JSON.parse(body));
  } catch {
    return null;
  }
}

function cardSkillsFromParsed(parsed: unknown): string[] | null {
  const skills = (parsed as { skills?: Array<{ id?: unknown }> } | null)?.skills;
  if (!Array.isArray(skills)) return null;
  return skills
    .map((s) => (typeof s.id === 'string' ? s.id : null))
    .filter((s): s is string => s !== null);
}

/**
 * Discovery card → `Skills: 7 exposed …` from the LIVE signed card.
 * The caption carries the staleness hint: the card response is edge-
 * cached (`Cache-Control: max-age=60` + a 10 s in-isolate KV cache),
 * so a fresh toggle can take about a minute to show here.
 */
export function cardSkillsFact(card: LiveResult | undefined): Fact | null {
  if (card === undefined || !card.ok) return null;
  // Prefer the pre-truncation parse — a real signed card exceeds the
  // 900-char display cap, so the `body` string is not valid JSON.
  const ids =
    card.json !== undefined ? cardSkillsFromParsed(card.json) : extractCardSkills(card.body);
  if (ids === null) return null;
  if (ids.length === 0) {
    return {
      label: 'Skills',
      value: '0 exposed — your signed card currently advertises no skills.',
      tone: 'amber',
      caption: 'live from your signed card · updates take ~1 min',
    };
  }
  const shown = ids.slice(0, 3);
  const hasMore = ids.length > shown.length;
  const suffix = hasMore ? ` · +${ids.length - shown.length} more` : '';
  return {
    label: 'Skills',
    value: `${ids.length} exposed: ${shown.join(' · ')}${suffix}`,
    caption: 'live from your signed card · updates take ~1 min',
    ...(hasMore ? { expandable_ids: ids } : {}),
  };
}

/**
 * Per-Run snapshot of the site's skills keyed on STATUS (not the raw
 * enabled toggle). Founder cross-check (2026-07-05): a skill can be
 * enabled yet NOT on the card (place_order awaiting a buyable product,
 * coming-soon skills) — the view must reconcile Settings' 7 rows with
 * the card's exposed set using the same status model Settings renders.
 */
export interface SkillStatusEntry {
  readonly skill_id: string;
  /** SkillStatus from the wire; older stubs may omit it (derived). */
  readonly status: string;
  /** Merchant-actionable next step (from the gating reason), if any. */
  readonly next_step?: string;
}

export interface SiteSkillsSnapshot {
  /** On the signed card; agents see these. */
  readonly live: readonly string[];
  /** Everything else, with why. */
  readonly not_live: readonly SkillStatusEntry[];
}

/** Short per-entry reason, mirroring Settings' hint copy. */
function reasonLabel(entry: SkillStatusEntry): string {
  switch (entry.status) {
    case 'disabled':
      return 'toggled off';
    case 'enabled_unsupported':
      return 'coming soon';
    default:
      return entry.next_step ?? 'not live yet';
  }
}

/**
 * Discovery-card fact reconciling Settings with the card: names every
 * skill that is NOT on the signed card, with its reason — toggled off,
 * coming soon, or the merchant-actionable next step ("add a buyable
 * product to go live"). Amber when any entry is merchant-actionable;
 * plain when only coming-soon rows remain (nothing to do).
 */
export function notOnCardFact(skills: SiteSkillsSnapshot | null): Fact | null {
  if (skills === null || skills.not_live.length === 0) return null;
  const total = skills.live.length + skills.not_live.length;
  const actionable = skills.not_live.filter((e) => e.status !== 'enabled_unsupported');
  // Group by reason, counts-first — seven per-skill parentheticals read
  // as a wall of text (2026-07-06 consolidation). Skill names move to a
  // small detail line, and only when the list is short.
  const groups = new Map<string, number>();
  for (const e of skills.not_live) {
    const r = reasonLabel(e);
    groups.set(r, (groups.get(r) ?? 0) + 1);
  }
  const grouped = [...groups.entries()].map(([r, n]) => `${n} ${r}`).join(' · ');
  const names =
    skills.not_live.length <= 3
      ? skills.not_live.map((e) => `${e.skill_id} (${reasonLabel(e)})`).join(' · ')
      : undefined;
  return {
    label: 'Not on card',
    value: `${skills.not_live.length} of ${total} skills not on your card — ${grouped}${actionable.length > 0 ? ' — Settings → Skills' : ''}`,
    ...(names !== undefined ? { detail: names } : {}),
    ...(actionable.length > 0 ? { tone: 'amber' as const } : {}),
  };
}

/**
 * Amber honesty note for the transactional ILLUSTRATION phases when
 * `place_order` is not LIVE on the signed card — for whatever reason:
 * toggled off, awaiting a buyable product, or tier-gated. The claim
 * stays card-level ("your signed card doesn't advertise checkout");
 * the MCP journey additionally proves the refusal end-to-end
 * (`unknown_tool`, journeys/claude.ts).
 */
export function checkoutNotLiveFact(skills: SiteSkillsSnapshot | null): Fact | null {
  if (skills === null || skills.live.includes('place_order')) return null;
  const entry = skills.not_live.find((e) => e.skill_id === 'place_order');
  if (entry === undefined) return null;
  return {
    label: 'Checkout',
    value: `place_order isn't live on your card (${reasonLabel(entry)}) — agents can't check out. The illustration continues anyway. Settings → Skills.`,
    tone: 'amber',
  };
}

/** Browse / catalog → `Products: 12 synced · e.g. "Model 100" $189.00`. */
export function summarizeProducts(liveProducts: LiveProducts): Fact | null {
  if (liveProducts.kind !== 'resolved') return null;
  const samples = summarizeProductSamples(liveProducts.products);
  if (samples.count === 0) {
    // The read-menu narration already states the empty store; a second
    // "0 synced" line duplicates it (2026-07-06 consolidation).
    return null;
  }
  const example =
    samples.first_title !== null
      ? ` · e.g. "${samples.first_title}"${samples.first_price !== null ? ` ${samples.first_price}` : ''}`
      : '';
  return {
    label: 'Products',
    value: `${samples.count} synced${example}`,
  };
}

/** Cart / pay → `Cart: 2 × Sample Product [sample] · $55.20 total`. */
export function cartFacts(cart: CartProduct): Fact | null {
  if (cart.unit_price_display.length === 0) return null;
  return {
    label: 'Cart',
    value: `2 × ${cart.title}${cart.is_sample ? ' [sample]' : ''} · ${cart.total_display} total`,
  };
}

/**
 * Requester facts from the endpoint row: the compact UA token (a
 * string op, never a parse) plus the amber masquerade fact when the
 * agent hides its identity.
 *
 * `verbose` (Discovery phase only — where the UA IS the story) adds
 * the documented-signature caption + the full UA detail line. Both are
 * visible-text honesty invariants there (ADR-0163 §7; test-pinned).
 * Every later phase repeats the same UA, so it compacts to the token —
 * the teaching happened once (founder verbosity feedback 2026-07-05).
 * The amber masquerade fact stays on EVERY phase it applies to, in
 * both variants — never compacted, never tooltip-only.
 */
export function requesterFacts(
  endpoint: EndpointInfo,
  opts: { readonly verbose?: boolean } = {},
): readonly Fact[] {
  if (endpoint.userAgent === undefined) return [];
  const facts: Fact[] = [];
  const compact = compactUaToken(endpoint.userAgent);
  const caption = endpoint.userAgentCaption;
  if (opts.verbose === true) {
    facts.push({
      label: 'Requester',
      value: compact,
      ...(caption !== undefined && caption !== null ? { caption } : {}),
      detail: endpoint.userAgent,
    });
  } else {
    facts.push({ label: 'Requester', value: compact });
  }
  if (endpoint.masqueradeNote !== undefined && endpoint.masqueradeNote !== null) {
    facts.push({ label: 'Identity', value: endpoint.masqueradeNote, tone: 'amber' });
  }
  return facts;
}

/**
 * The identifying token of a documented UA. Most agent UAs carry the
 * bot token late in the string (`Mozilla/5.0 … compatible; Claude-User/1.0; …`),
 * so prefer the LAST `Word/version`-shaped token that is not a stock
 * browser-stack token; fall back to the leading token.
 */
const STOCK_TOKENS = new Set([
  'Mozilla',
  'AppleWebKit',
  'Chrome',
  'Safari',
  'Version',
  'Gecko',
  'KHTML',
  'Firefox',
  'Edg',
]);

export function compactUaToken(ua: string): string {
  const matches = ua.match(/[A-Za-z][A-Za-z0-9._-]*\/[0-9][A-Za-z0-9._-]*/g) ?? [];
  for (let i = matches.length - 1; i >= 0; i--) {
    const token = matches[i]!;
    const name = token.split('/', 1)[0]!;
    if (!STOCK_TOKENS.has(name)) return token;
  }
  return ua.split(' ', 1)[0] ?? ua;
}
